import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import useDebounce from '../../hooks/useDebounce';
import { useDidUpdateEffect } from '../../hooks/useDidUpdateEffect';

const SearchInput = ({
  placeholder,
  onSearchChange,
  className,
  type,
  debounce = true,
  initialSearchString,
}) => {
  // Search string
  const [searchString, setSearchString] = useState(initialSearchString);
  const skipNextSearchTriggerRef = useRef(false);

  useEffect(() => {
    // Keep local input in sync with external state (e.g. URL restore on back),
    // but do not trigger a new search callback for this prop-driven update.
    // Only set skip when we actually change local state. If the value is already
    // the same, React may bail out without re-running effects — then skip would
    // stay true and swallow the user's next debounced search (e.g. after delete).
    const next = initialSearchString ?? '';
    setSearchString((prev) => {
      if (prev === next) return prev;
      skipNextSearchTriggerRef.current = true;
      return next;
    });
  }, [initialSearchString]);

  const setSearch = () => {
    if (skipNextSearchTriggerRef.current) {
      skipNextSearchTriggerRef.current = false;
      return;
    }
    onSearchChange(searchString);
  };
  const pauseAndRestart = useDebounce(setSearch);
  useDidUpdateEffect(() => {
    if (debounce) pauseAndRestart();
    else setSearch();
  }, [debounce, searchString, pauseAndRestart]);

  return (
    <input
      type={type || 'text'}
      className={className}
      placeholder={placeholder || 'Search here'}
      value={searchString ?? ''}
      onChange={(e) => setSearchString(e?.target?.value)}
    />
  );
};

SearchInput.propTypes = {
  onSearchChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  type: PropTypes.string,
  debounce: PropTypes.bool,
  initialSearchString: PropTypes.string,
};

export default SearchInput;
